import React, { useState, useRef } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import "./ApplicantRegister.css";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import WisdomLogo from "../../medias/images/Wisdom logo transparent.png";
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import { requestCreateApplicant } from "../../services/wisdomServices/applicantService";
import { Calendar } from "primereact/calendar";
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'

function ApplicantRegister() {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [confirmPassword, setConfirmPassword] = useState("");
  const navigate = useNavigate();
  const toast = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phoneNumber: "",
    city: "",
    zipcode: "",
    validation: "",
  });

  const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9])(?=.{8,})/;
  const getEmailNameParts = (email) => {
    if (!email) return [];

    const namePart = email.split("@")[0];

    return namePart
      .split(/[._-]/)
      .filter(Boolean)
      .map((part) => part.toLowerCase());
  };

  const emailNameParts = getEmailNameParts(formData.email);

  const containsEmailName = emailNameParts.some(
    (part) =>
      part.length >= 3 &&
      formData.password.toLowerCase().includes(part)
  );

  const isStrongPassword = strongPasswordRegex.test(formData.password);

  const isPasswordValid = isStrongPassword && !containsEmailName;

  const passwordsMatch =
    formData.password &&
    confirmPassword &&
    formData.password === confirmPassword;

  const passwordsDoNotMatch =
    confirmPassword &&
    formData.password !== confirmPassword;

  const showError = (message) => {
    toast.current.show({
      severity: "error",
      summary: "Error",
      detail: message,
      life: 3000
    });
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const getFingerprint = async () => {
    const fp = await FingerprintJS.load()
    const result = await fp.get()
    return result.visitorId
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!executeRecaptcha) {
      showError("Recaptcha not ready");
      return;
    }

    
    const {firstName, lastName, email, password, phoneNumber, city, zipcode } = formData;
    
    if ( !firstName || !lastName || !email || !password || !phoneNumber || !city || !zipcode) {
      showError("All fields are required");
      return;
    }

    if (formData.password !== confirmPassword) {
      showError("Passwords do not match");
      return;
    }

    if (!isPasswordValid) {
      showError("Password does not meet security requirements");
      return;
    }
    
    const deviceFingerprint = await getFingerprint()
    try {
      setIsLoading(true);
      const token = await executeRecaptcha("register");
      const payload = {
        ...formData,
        deviceFingerprint: deviceFingerprint,
        captcha: token,
      };
      payload.phoneNumber = "+" + payload.phoneNumber; // Ensure phone number is in E.164 format
      await requestCreateApplicant(payload)

      navigate("/applicant-signin");
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "";

    return date.toISOString().split("T")[0];
  };

  return (
    <div className="register-container">
      <Toast ref={toast} position="top-center" />
      <form onSubmit={handleSubmit} onReset={() => navigate("/applicant-signin")} method="post" className="register-card">
        <div className="register-header-img">
          <img className="wisdom-logo-img" src={WisdomLogo} alt="Wisdom Logo" />
        </div>
        <div className="register-header-text">
          <h2>Create Your Account</h2>
          <p>Please fill in your information to sign up</p>
        </div>

        <div className="form-grid">

          <div className="form-field">
            <label htmlFor="first-name-form-field">First Name</label>
            <InputText
                id="first-name-form-field"
                value={formData.firstName}
                onChange={(e) => handleChange("firstName", e.target.value)}
            />
          </div>

          <div className="form-field">
            <label htmlFor="last-name-form-field">Last Name</label>
            <InputText
                id="last-name-form-field"
                value={formData.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
            />
          </div>

          <div className="form-field full-width">
            <label htmlFor="email-form-field">Email</label>
            <InputText
                id="email-form-field"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
            />
          </div>

          <div className="form-field full-width">
            <label htmlFor="password-form-field">Password</label>
            <Password
                inputId="password-form-field"
                value={formData.password}
                onChange={(e) => handleChange("password", e.target.value)}
                toggleMask
                feedback={false}
                className="password-form-field"
            />
          </div>
          {formData.password && !isStrongPassword && (
            <small className="password-error">
              Password must contain:
              <br />• Minimum 8 characters
              <br />• At least 1 uppercase letter
              <br />• At least 1 lowercase letter
              <br />• At least 1 special character
            </small>
          )}

          {formData.password && containsEmailName && (
            <small className="password-error">
              <br />• Password cannot contain parts of your email.
            </small>
          )}
          <div className="form-field full-width">
            <label htmlFor="confirm-password-form-field">Confirm Password</label>
            <Password
              inputId="confirm-password-form-field"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              toggleMask
              feedback={false}
            />
            {passwordsDoNotMatch && (
              <small className="password-error">
                Passwords do not match
              </small>
            )}

            {passwordsMatch && (
              <small className="password-success">
                Passwords match
              </small>
            )}
          </div>
          <div className="form-field phone-input-field">
            <label htmlFor="phone-form-field">Phone Number</label>
            <PhoneInput
              country="us"
              value={formData.phoneNumber}
              onChange={(value, country) => {
                handleChange("phoneNumber", value);
                handleChange("country", country?.name || "");
              }}
              inputProps={{
                id: "phone-form-field",
                name: "phoneNumber",
                autoComplete: "tel",
              }}
            />
          </div>

          <div className="form-field">
            <label htmlFor="zip-code-form-field">ZIP Code</label>
            <InputText
                id="zip-code-form-field"
                value={formData.zipcode}
                onChange={(e) => handleChange("zipcode", e.target.value)}
            />
          </div>

          <div className="form-field full-width">
            <label htmlFor="city-form-field">City/Country</label>
            <InputText
                id="city-form-field"
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
            />
          </div>

          <div className="form-field full-width" id="type-confirm-input-box">
            <label htmlFor="type-confirm-field">Type "confirm"</label>
            <InputText
                id="type-confirm-field"
                value={formData.validation}
                onChange={(e) => handleChange("validation", e.target.value)}
            />
          </div>

        </div>

        <div className="register-actions">
          <Button
            label="Back to Login"
            icon="pi pi-arrow-left"
            type="reset"
            severity="secondary"
          />

          <Button
            label="Sign Up"
            type="submit"
            icon="pi pi-user-plus"
            severity="success"
            loading={isLoading}
            disabled={
              isLoading ||
              !isPasswordValid ||
              !passwordsMatch
            }
          />
        </div>

        <div className="login-redirect">
          Already have an account?{" "}
          <span onClick={() => navigate("/applicant-signin")}>
            Login
          </span>
        </div>
      </form>
    </div>
  );
}

export default ApplicantRegister;